import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { FormControl } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { IDisplayData } from '@whetstoneeducation/hero-common';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

/**
 * Basic Dropdown using HTML Select for performance
 */
@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.template.html',
  styleUrls: ['./autocomplete.scss'],
})
export class AppAutocompleteComponent {
  @Input()
  public label: string;

  @Input()
  public placeholder: string;

  @Input()
  public options: IDisplayData[] = [];

  @Output()
  public selectionChange: EventEmitter<unknown>;

  @Input()
  public value: unknown;

  @Input()
  public inputDisplay: IDisplayData;

  @Output()
  public valueChange: EventEmitter<unknown>;

  @ViewChild('autoInput')
  private autoInput!: ElementRef<HTMLInputElement>;

  filteredOptions: Observable<IDisplayData[]>;
  formControl: FormControl<IDisplayData | null> = new FormControl<IDisplayData | null>(null);

  constructor() {
    this.selectionChange = new EventEmitter<unknown>();
    this.valueChange = new EventEmitter<unknown>();
  }

  get formValue() {
    return this.formControl.value;
  }

  ngOnInit() {
    let existingValue = null;

    if(this.inputDisplay){
      this.formControl.setValue(this.inputDisplay);
      existingValue = Number(this.inputDisplay.value);
      this.selectionChange.emit(existingValue);
      this.valueChange.emit(existingValue);
    }

    this.filteredOptions = this.formControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', existingValue)),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.options?.firstChange && !changes.value?.currentValue) {
      this.clearDropdown();
    }
  }

  displaySelectedOption(option: IDisplayData | null): string {
    return option?.display || '';
  }

  onSelected(event: MatAutocompleteSelectedEvent) {
    const selectedOption: IDisplayData = event.option.value;
    const selectedValue = selectedOption.value;

    this.filteredOptions = this.formControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value, selectedValue)),
    );

    this.selectionChange.emit(selectedValue);
    this.valueChange.emit(selectedValue);
  }

  onClear(event: MouseEvent) {
    this.clearDropdown();
    this.valueChange.emit(null);

    event.stopPropagation();
  }

  private _filter(value: string | IDisplayData, selectedValue: string | null): IDisplayData[] {
    if (!this.options) {
      return [];
    } else if (!value) {
      return this.options;
    }

    const filterValue = typeof value === 'string' ? value.toLowerCase() : value.display.toLowerCase();

    return this.options.filter(option => option.display.toLowerCase().includes(filterValue) &&
      (selectedValue === null || option.value.toString() !== selectedValue));
  }

  private clearDropdown() {
    this.selectionChange.emit(null);
    this.formControl.setValue(null);

    if (this.autoInput) {
      this.autoInput.nativeElement.value = null;
      this.autoInput.nativeElement.dispatchEvent(new Event('input', {
        'bubbles': true,
        'cancelable': true,
      }));
    }
  }
}
